/**
 * Claims and ERA (WP4-CLAIMS, BUILD-PLAN M20). `/claims` is the Claims
 * Manager (board and table, the claim sheet, leak lanes, A/R targets);
 * `/claims/era` is ERA posting (balancing equation, exceptions, shadow
 * posting where the PMS owns money). Matches prototypes/billing.html,
 * tabs 2 and 3.
 *
 * `/claims/batch` is the send queue (Dentrix's Batch Processor) and
 * `/claims/insurance-payment` insurance checks (Batch Insurance Payment Entry).
 *
 * Deep links: `/claims?claim=<id>`, `/claims?view=table`, `/claims/era?era=<id>`.
 */
import { defineManifest } from '@velora/contracts';
import { defineWebModule } from '../../app/module.js';

export default defineWebModule({
  manifest: defineManifest({
    id: 'claims',
    title: 'Claims',
    version: '0.1.0',
    owner: 'WP4-CLAIMS',
    entitlementKey: null,
    permissions: ['claims.view', 'claims.send'],
    routes: [
      {
        id: 'claims',
        path: '/claims',
        title: 'Claims',
        officeScope: 'location-or-all',
        permission: 'claims.view',
        readsPhi: true,
      },
      {
        id: 'work',
        path: '/claims/work',
        title: 'Claims work queues',
        officeScope: 'location-or-all',
        permission: 'claims.view',
        readsPhi: true,
      },
      {
        id: 'era',
        path: '/claims/era',
        title: 'ERA posting',
        officeScope: 'location-or-all',
        permission: 'claims.view',
        readsPhi: true,
      },
      {
        id: 'batch',
        path: '/claims/batch',
        title: 'Send queue',
        officeScope: 'location-or-all',
        permission: 'claims.view',
        readsPhi: true,
      },
      {
        id: 'inspay',
        path: '/claims/insurance-payment',
        title: 'Insurance checks',
        officeScope: 'location-or-all',
        permission: 'claims.view',
        readsPhi: true,
      },
    ],
    nav: [
      {
        label: 'Claims',
        group: 'revenue',
        routeId: 'claims',
        icon: 'file-text',
        order: 15,
        permission: 'claims.view',
      },
      {
        label: 'Claims work queues',
        group: 'revenue',
        routeId: 'work',
        icon: 'list-checks',
        order: 16,
        permission: 'claims.view',
      },
    ],
    commands: [
      {
        id: 'claims.open',
        title: 'Go to Claims',
        keywords: ['claims', 'insurance', 'rejections', 'denials', 'scrubber', 'clearinghouse'],
        routeId: 'claims',
        permission: 'claims.view',
      },
      {
        id: 'claims.work',
        title: 'Claims needing attention',
        keywords: [
          'rejections',
          'follow-up',
          'aging',
          'payer list',
          'claims report',
          'unbilled',
          'outstanding claims',
          'unsent',
          'paid not posted',
        ],
        routeId: 'work',
        permission: 'claims.view',
      },
      {
        id: 'claims.era',
        title: 'Post ERAs',
        keywords: ['era', '835', 'remittance', 'eob', 'insurance payment'],
        routeId: 'era',
        permission: 'claims.view',
      },
      {
        id: 'claims.batch',
        title: 'Send queue (Batch Processor)',
        keywords: ['batch', 'batch processor', 'send claims', 'print claims', 'queue'],
        routeId: 'batch',
        permission: 'claims.view',
      },
      {
        id: 'claims.inspay',
        title: 'Insurance checks (bulk insurance payment)',
        keywords: [
          'insurance payment',
          'bulk',
          'batch insurance payment',
          'eob',
          'check',
          'deposit',
        ],
        routeId: 'inspay',
        permission: 'claims.view',
      },
    ],
    shortcuts: [{ keys: 'g k', description: 'Go to Claims', routeId: 'claims' }],
    sseEntityKeys: ['claim', 'era', 'procedure'],
    phiReadPoints: [
      'claims.board',
      'claims.open',
      'claims.patient',
      'era.open',
      'claims.needs_attention',
      'claims.queues',
      'claims.followups',
      'claims.image_suggestions',
    ],
  }),
  pages: {
    claims: () => import('./ClaimsPage.js'),
    era: () => import('./EraPage.js'),
    work: () => import('./WorkPage.js'),
    batch: () => import('./BatchPage.js'),
    inspay: () => import('./InsurancePaymentPage.js'),
  },
});
